import { alpha, Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OcrLine } from 'services/imports/runOcr';

const MAX_LABEL_CHARS = 60;

const truncate = (text: string): string => (
  text.length > MAX_LABEL_CHARS ? `${text.slice(0, MAX_LABEL_CHARS - 1)}…` : text
);

/** Normalized (0..1) bbox values become percentages of the image wrapper, so zoom needs no recompute. */
const toPercent = (value: number): string => `${value * 100}%`;

/** Outline styles differ by pattern as well as colour: selected is solid and thick, linked is dashed, unused is hairline. */
const useStyles = makeStyles((theme: Theme) => createStyles({
  box: {
    position: 'absolute',
    boxSizing: 'border-box',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    cursor: 'pointer',
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    '&:focus': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  linked: {
    border: `1px dashed ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  selected: {
    zIndex: 1,
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
  },
}));

type SourceBoxProps = {
  line: OcrLine;
  index: number;
  selected: boolean;
  linked: boolean;
  tabStop: boolean;
  onSelect: (line: OcrLine) => void;
  onNavigate: (index: number, step: number) => void;
};

const NEXT_KEYS: readonly string[] = ['ArrowRight', 'ArrowDown'];
const PREVIOUS_KEYS: readonly string[] = ['ArrowLeft', 'ArrowUp'];

/** One OCR line drawn over the page image. Only one box is a tab stop; arrow keys move between boxes. */
export const SourceBox = ({
  line, index, selected, linked, tabStop, onSelect, onNavigate,
}: SourceBoxProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { left, top, width, height } = line.bbox;
  const className = [
    classes.box,
    linked ? classes.linked : '',
    selected ? classes.selected : '',
  ].filter(Boolean).join(' ');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (NEXT_KEYS.includes(event.key)) {
      event.preventDefault();
      onNavigate(index, 1);
    } else if (PREVIOUS_KEYS.includes(event.key)) {
      event.preventDefault();
      onNavigate(index, -1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(line);
    }
  };

  return (
    <div
      role="button"
      tabIndex={tabStop ? 0 : -1}
      data-box-index={index}
      aria-label={t('imports:viewer.boxLabel', { text: truncate(line.text) })}
      aria-pressed={selected}
      title={line.text}
      className={className}
      style={{
        left: toPercent(left), top: toPercent(top), width: toPercent(width), height: toPercent(height),
      }}
      onClick={() => onSelect(line)}
      onKeyDown={handleKeyDown}
    />
  );
};
